import ChildrenTabList from "@/elements/components/shared/children-tab-list";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { Button } from "@/elements/components/ui/button";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { Fragment, useMemo, useState } from "react";
import { ScrollView, View, Text, Pressable } from "react-native";
import { api } from "../utils/convex-api";
import { Id } from "@teachloop/backend/_generated/dataModel";
import useLessonQuantities from "@/elements/hooks/use-lesson-quantities";
import useLessonCreditsBuilder from "@/elements/hooks/use-lesson-credits-builder";
import LessonTypeQuantitySelector from "./lesson-type-quantity-selector";
import { useSheet } from "../shared/sheet-provider";
import { SheetIds } from "../utils/sheet-ids";
import {
  SingleSelectSheetPayload,
  SingleSelectSheetResult,
} from "../sheets/single-select-sheet";
import StripePaymentForm from "./stripe-payment-form";

type BuyCreditsFormProps = {
  childList: typeof api.parent.buyCredits.queries.getMyChildren._returnType;
  selectedChildId: Id<"students">;
  setSelectedChildId: (childId: Id<"students">) => void;
  lessonPrices: typeof api.parent.buyCredits.queries.getLessonPricesForStudents._returnType;
  returnTo?: string;
  bookingData?: string;
};

const OPTIONS = [
  {
    label: "No lessons",
    value: 0,
  },
  {
    label: "1 lesson",
    value: 1,
  },
  {
    label: "2 lessons",
    value: 2,
  },
  {
    label: "3 lessons",
    value: 3,
  },
  {
    label: "4 lessons",
    value: 4,
  },
  {
    label: "5 lessons",
    value: 5,
  },
  {
    label: "6 lessons",
    value: 6,
  },
  {
    label: "7 lessons",
    value: 7,
  },
  {
    label: "8 lessons",
    value: 8,
  },
  {
    label: "9 lessons",
    value: 9,
  },
  {
    label: "10 lessons",
    value: 10,
  },
];

function BuyCreditsForm({
  childList,
  selectedChildId,
  setSelectedChildId,
  lessonPrices,
  returnTo,
  bookingData,
}: BuyCreditsFormProps) {
  const { data: creditBalance } = useQuery(
    convexQuery(api.parent.buyCredits.queries.getCreditBalance, {})
  );
  const studentIds = useMemo(
    () => childList.map((child) => child._id),
    [childList]
  );
  const { open } = useSheet<SingleSelectSheetPayload, SingleSelectSheetResult>(
    SheetIds.SINGLE_SELECT
  );
  const { open: openLessonPriceList } = useSheet(SheetIds.LESSON_PRICE_LIST);
  const { lessonQuantities, setQuantity } = useLessonQuantities(studentIds);
  const { totalCredits, totalCents, items } = useLessonCreditsBuilder(
    childList,
    lessonPrices,
    lessonQuantities
  );
  const inPersonLessonCreditsForSelectedStudent =
    lessonPrices[selectedChildId]?.inPerson?.totalCredits ?? 0;
  const remoteLessonCreditsForSelectedStudent =
    lessonPrices[selectedChildId]?.remote?.totalCredits ?? 0;

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [purchaseOrderId, setPurchaseOrderId] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  const { mutateAsync: createPurchaseOrder, isPending: creatingPurchaseOrder } =
    useMutation({
      mutationFn: useConvexMutation(
        api.parent.buyCredits.mutations.createPurchaseOrder
      ),
    });
  const { mutateAsync: createPaymentIntent, isPending: creatingPaymentIntent } =
    useMutation({
      mutationFn: useConvexAction(
        api.parent.buyCredits.actions.createPaymentIntent
      ),
    });

  const onBuyCredits = async () => {
    try {
      const response = await createPurchaseOrder({
        totalCredits,
        totalCents,
        items,
      });
      console.log({ response });

      const paymentIntent = await createPaymentIntent({
        purchaseOrderId: response,
      });
      console.log({ paymentIntent });

      if (paymentIntent.clientSecret) {
        setClientSecret(paymentIntent.clientSecret);
        setPurchaseOrderId(response);
        setPaymentIntentId(paymentIntent.paymentIntentId);
        setShowPaymentForm(true);
      }
    } catch (error) {
      console.error("Error creating payment intent:", error);
    }
  };

  const onPaymentSuccess = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
    setPurchaseOrderId(null);
    setPaymentIntentId(null);
    // Success handling is done in the payment form component
  };

  const onPaymentError = (error: string) => {
    console.error("Payment error:", error);
    // Error handling could be improved here
  };

  const onOpenLessonCountSelectSheet = async (
    mode: "inPerson" | "remote",
    currentValue: number
  ) => {
    if (mode === "inPerson") {
      const result = await open({
        title: "No of in-person lessons",
        subtitle:
          "30 AED surcharge is added to each In-person lesson in order to cover the tutor's travel costs. We calculate lessons in 60-minute or 30-minute blocks. If you wish to book a 120 minute lesson you will need to buy 2 lessons, if you wish to book a 90 minute lesson, you will need to buy 1 lesson + 30 minute lesson.",
        options: OPTIONS,
        preSelectedOption: currentValue,
        uiMode: "radio",
      });
      if (result.selectedOption != null) {
        setQuantity(
          selectedChildId,
          "inPerson",
          result.selectedOption as number
        );
      }
    } else {
      const result = await open({
        title: "No of remote lessons",
        subtitle:
          "We calculate lessons in 60-minute or 30-minute blocks. If you wish to book a 120 minute lesson you will need to buy 2 lessons, if you wish to book a 90 minute lesson, you will need to buy 1 lesson + 30 minute lesson.",
        options: OPTIONS,
        preSelectedOption: currentValue,
        uiMode: "radio",
      });
      if (result.selectedOption != null) {
        setQuantity(selectedChildId, "remote", result.selectedOption as number);
      }
    }
  };

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <ResponsiveContent>
        <View className="mx-5">
          <View className="gap-8">
            <View className="gap-1">
              <Text className="text-[28px] font-zilla-slab-medium font-medium text-black leading-[1.2]">
                Lesson credits
              </Text>
              <Text className="text-xl font-geologica-light font-normal text-black leading-[1.5]">
                Get lesson credits to book with ease.
              </Text>
            </View>
            <View>
              <View className="gap-4">
                <View className="px-4 py-6 bg-teachLoop-creamDark rounded-lg">
                  <View className="flex-row items-center gap-4 justify-center">
                    <Text className="text-base font-geologica-light font-normal text-black leading-[1.5]">
                      Credits available
                    </Text>
                    <Text className="text-[32px] font-geologica-medium font-medium text-black leading-[1.5]">
                      {creditBalance?.balance}
                    </Text>
                  </View>
                </View>
                <Pressable onPress={() => openLessonPriceList()}>
                  <Text className="text-center text-base font-geologica-medium font-medium text-teal-600 leading-[24px]">
                    See lesson price list
                  </Text>
                </Pressable>
              </View>
            </View>
            <View className="gap-4">
              <View>
                <Text className="text-base font-geologica-medium font-medium text-black leading-[1.5]">
                  Credits are calculated based on your child's education level.
                  One lesson is calculated as 60 minutes.
                </Text>
              </View>
              <View className="gap-2">
                <View>
                  <ChildrenTabList
                    childList={childList}
                    selectedChildId={selectedChildId}
                    setSelectedChildId={
                      setSelectedChildId as (
                        childId: Id<"students"> | null
                      ) => void
                    }
                    noInactiveBorder
                  />
                </View>
                <View className="h-[1px] bg-black opacity-10" />
              </View>
              <View className="gap-2">
                <LessonTypeQuantitySelector
                  studentId={selectedChildId}
                  lessonQuantities={lessonQuantities}
                  inPersonLessonCreditsForSelectedStudent={
                    inPersonLessonCreditsForSelectedStudent
                  }
                  remoteLessonCreditsForSelectedStudent={
                    remoteLessonCreditsForSelectedStudent
                  }
                  onOpenLessonCountSelectSheet={onOpenLessonCountSelectSheet}
                />

                <View>
                  <View className="h-[1px] bg-black opacity-10" />

                  {childList
                    .map((child) => {
                      const childItems = items.filter(
                        (item) => item.studentId === child._id
                      );
                      const totalCreditsForChild = childItems.reduce(
                        (acc, item) =>
                          acc + item.priceSnapshot.totalCredits * item.quantity,
                        0
                      );

                      if (totalCreditsForChild === 0) return null;

                      return (
                        <Fragment key={child._id}>
                          <View className="flex-row items-center justify-between py-3">
                            <Text className="text-base font-geologica-medium font-medium text-gray-500 leading-[1.5] flex-1">
                              Credits required for {child.name}
                            </Text>
                            <Text className="text-base font-geologica-medium font-medium text-gray-500 leading-[1.5] flex-[0.5] text-center">
                              {totalCreditsForChild}
                            </Text>
                          </View>
                          <View className="h-[1px] bg-black opacity-10" />
                        </Fragment>
                      );
                    })
                    .filter(Boolean)}

                  <View className="flex-row items-center justify-between py-4">
                    <Text className="text-base font-geologica-medium font-medium text-black leading-[1.5] flex-1">
                      Total credits
                    </Text>
                    <Text className="text-base font-geologica-medium font-medium text-black leading-[1.5] flex-[0.5] text-center">
                      {totalCredits}
                    </Text>
                  </View>
                  <View className="h-[1px] bg-black opacity-10" />
                  <View className="flex-row items-centerpy-3 flex-1 items-center py-2">
                    <Text className="text-base font-geologica-medium font-medium text-black leading-[1.5] flex-1">
                      Total cost
                    </Text>
                    <View className="flex-row items-center justify-end gap-2 flex-[0.5]">
                      <Text className="text-base font-geologica-medium font-medium text-primary-0 leading-[1.5] text-center">
                        AED
                      </Text>
                      <View
                        className="border px-4 py-3 rounded-xl min-w-[100px] bg-teachLoop-creamLight"
                        style={{ borderColor: "rgba(239, 233, 230, 1)" }}
                      >
                        <Text className="text-base font-geologica-medium font-medium text-primary-0 leading-[1.5]">
                          {totalCents / 100}
                        </Text>
                      </View>
                    </View>
                  </View>
                  <View className="h-[1px] bg-black opacity-10" />
                </View>
              </View>
            </View>

            <View className="flex-1">
              <Button
                size="md"
                intent="secondary"
                className="w-full"
                disabled={
                  items.length === 0 ||
                  creatingPurchaseOrder ||
                  creatingPaymentIntent
                }
                onPress={onBuyCredits}
              >
                <Button.Text>
                  {creatingPurchaseOrder || creatingPaymentIntent
                    ? "Setting up payment..."
                    : "Buy credits"}
                </Button.Text>
              </Button>
            </View>

            {/* Payment Modal */}
            {showPaymentForm &&
              clientSecret &&
              purchaseOrderId &&
              paymentIntentId && (
                <StripePaymentForm
                  clientSecret={clientSecret}
                  originalAmount={totalCents}
                  purchaseOrderId={purchaseOrderId}
                  paymentIntentId={paymentIntentId}
                  returnTo={returnTo}
                  bookingData={bookingData}
                  onSuccess={onPaymentSuccess}
                  onError={onPaymentError}
                  onClose={() => {
                    setShowPaymentForm(false);
                    setClientSecret(null);
                    setPurchaseOrderId(null);
                    setPaymentIntentId(null);
                  }}
                />
              )}
          </View>
          <View className="h-8" />
        </View>
      </ResponsiveContent>
    </ScrollView>
  );
}

export default BuyCreditsForm;
